import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import type { IApiResponse, IApiResponsePageable } from '@/shared/types/api'
import type {
  IDeferralJournalItem,
  IDeferralListParams,
  IDeferralSummary,
  IDeferralSummaryParams,
  IManualDeferralRequest,
  IManualDeferralResult,
} from '../model/deferral.type'

/** Unset filters are dropped; `sort` repeats per entry. */
const toQuery = ({
  sort,
  ...params
}: IDeferralSummaryParams & {
  type?: string
  page?: number
  size?: number
  sort?: string[]
}) => {
  const query = new URLSearchParams()
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    const text = String(value).trim()
    if (text) query.append(key, text)
  })
  sort?.forEach((entry) => query.append('sort', entry))
  return query.toString()
}

// ── Raw API ───────────────────────────────────────────────────────────────────

export const deferralApi = {
  createManualDeferral: async (data: IManualDeferralRequest) => {
    const response = await apiVisitEngine.post<
      IApiResponse<IManualDeferralResult>
    >('/v1/deferrals/manual', data, {
      _skipGlobalErrorHandler: true,
    } as any)
    return response.data
  },

  /** One sub-tab of «Отводы» — scoped to the user's organization. */
  getDeferrals: async (params: IDeferralListParams) => {
    const response = await apiVisitEngine.get<
      IApiResponsePageable<IDeferralJournalItem>
    >(`/v1/deferrals?${toQuery(params)}`)
    return response.data.data
  },

  /** Sub-tab counters; same filters as the list so they match `totalElements`. */
  getDeferralsSummary: async (params: IDeferralSummaryParams) => {
    const response = await apiVisitEngine.get<IApiResponse<IDeferralSummary>>(
      `/v1/deferrals/summary?${toQuery(params)}`,
    )
    return response.data.data
  },

  /** Every deferral of the donor, any organization, newest first. */
  getDonorDeferrals: async (donorId: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<IDeferralJournalItem[]>
    >(`/v1/donors/${donorId}/deferrals`)
    return response.data.data
  },
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export const useGetDeferralsQuery = (
  params: IDeferralListParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: queryKeys.deferrals.list(params),
    queryFn: () => deferralApi.getDeferrals(params),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  })

export const useGetDeferralsSummaryQuery = (
  params: IDeferralSummaryParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: queryKeys.deferrals.summary(params),
    queryFn: () => deferralApi.getDeferralsSummary(params),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  })

export const useGetDonorDeferralsQuery = (donorId: string) =>
  useQuery({
    queryKey: queryKeys.deferrals.donor(donorId),
    queryFn: () => deferralApi.getDonorDeferrals(donorId),
    enabled: !!donorId,
  })

/**
 * Defers a donor by hand (screening temperature check, ...). The visit is
 * identified in the body, so the same endpoint serves every step.
 */
export const useCreateManualDeferralMutation = (
  visitId: string,
  donorId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: IManualDeferralRequest) =>
      deferralApi.createManualDeferral(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.visits.examinations(visitId),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.deferrals.all })
    },
  })
}
